import qs from 'qs';
import { FindAllProjectsFilters, FindAllProjectsResponse, ProjectDTO, ProjectStateEnum } from '../app/pages/projects/dto';
import { BindCustomerFormData, ProjectEditFormData, ProjectEmailsFormData } from '../app/pages/projects/editProject/validation';
import { ProjectCreationFormData } from '../app/pages/projects/newProject/validation';
import { UserDTO } from '../app/pages/users/dto';
import { baseApi } from './_baseQuery';


export const projectApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        findAllProjects: build.query<{ projects: FindAllProjectsResponse, teamLeaders: { user: UserDTO, avatar: string }[], commercials: { user: UserDTO, avatar: string }[] }, FindAllProjectsFilters>({
            async queryFn(filters, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const projectsResponse = await fetchWithBQ(
                        {
                            url: "/projects",
                            params: qs.stringify({ ...filters }, {
                                arrayFormat: "repeat",
                                skipNulls: true,
                                allowEmptyArrays: false,
                                encode: true,
                            })
                        }
                    );
                    if (projectsResponse.error) return { error: projectsResponse.error };

                    const projects = projectsResponse.data as FindAllProjectsResponse;
                    const projectsData = projects.data;

                    const commercialIds = [...new Set(projectsData?.flatMap(p => p.commercialIds || []).filter(id => id !== null))];
                    const teamLeaderIds = [...new Set(projectsData?.flatMap(p => p.teamLeaderId || []).filter(id => id !== null))];

                    const commercialsResponse = await Promise.all(
                        commercialIds.map(async id => {
                            const userRes = await fetchWithBQ(`/users/${id}`);
                            if (userRes.error) return { error: userRes.error };

                            const user = userRes.data as UserDTO;

                            let avatar: string | null = null;
                            if (user.avatarObjectId) {
                                const avatarRes = await fetchWithBQ("/objects/file/" + user.avatarObjectId);
                                if (!avatarRes.error) {
                                    avatar = avatarRes.data as string;
                                }
                            }

                            return {
                                data: {
                                    user: user,
                                    avatar: avatar || ''
                                }
                            };
                        })
                    );

                    const teamLeaderResponse = await Promise.all(
                        teamLeaderIds.map(async id => {
                            const userRes = await fetchWithBQ(`/users/${id}`);
                            if (userRes.error) return { error: userRes.error };

                            const user = userRes.data as UserDTO;

                            let avatar: string | null = null;
                            if (user.avatarObjectId) {
                                const avatarRes = await fetchWithBQ("/objects/file/" + user.avatarObjectId);
                                if (!avatarRes.error) {
                                    avatar = avatarRes.data as string;
                                }
                            }

                            return {
                                data: {
                                    user: user,
                                    avatar: avatar || ''
                                }
                            };
                        })
                    );

                    const commercials = commercialsResponse
                        .filter(response => response && !response.error)
                        .map(response => response.data as { user: UserDTO, avatar: string });

                    const teamLeaders = teamLeaderResponse
                        .filter(response => response && !response.error)
                        .map(response => response.data as { user: UserDTO, avatar: string });


                    return {
                        data: {
                            projects,
                            teamLeaders: teamLeaders,
                            commercials: commercials
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch users or avatars',
                            data: error
                        }
                    };
                }
            },
            providesTags: [{ type: 'Projects', id: 'LIST' }]
        }),
        findSuspendedProjects: build.query<FindAllProjectsResponse, void>({
            query: () => ({
                url: "/projects",
                params: {
                    state: ProjectStateEnum.SUSPENDED,
                }
            }),
            providesTags: [{ type: 'Projects', id: 'LIST' }]
        }),
        deleteProject: build.mutation<{ success: boolean }, { projectId: string }>({
            query: ({ projectId }) => ({
                url: `projects/${projectId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Projects', id: 'LIST' }],
        }),
        createProject: build.mutation<{ projectId: string }, ProjectCreationFormData>({
            query: (project) => ({
                url: `projects`,
                method: 'POST',
                body: project
            }),
            invalidatesTags: (_result, _error, _) => [{ type: 'Projects', id: 'LIST' }],
        }),
        findOneProject: build.query<ProjectDTO, { projectId: string }>({
            query: ({ projectId }) => ({
                url: `/projects/${projectId}`,
            }),
            providesTags: (_result, _error, { projectId }) => [{ type: 'Projects', id: projectId }],
        }),
        editProject: build.mutation<void, { id: string; data: Partial<ProjectEditFormData> | Partial<ProjectEmailsFormData> }>({
            query: ({ id, data }) => ({
                url: `projects/${id}`,
                method: 'PUT',
                body: data
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Projects', id }, { type: 'Projects', id: 'LIST' }],
        }),
        bindCustomer: build.mutation<void, { id: string; data: Partial<BindCustomerFormData> }>({
            query: ({ id, data }) => ({
                url: `projects/${id}/bind-customer`,
                method: 'PUT',
                body: data
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Projects', id }, { type: 'Projects', id: 'LIST' }],
        }),
        updateTeamLeader: build.mutation<void, { id: string; data: { teamLeaderId: string | null } }>({
            query: ({ id, data }) => ({
                url: `projects/${id}/update-team-leader`,
                method: 'PUT',
                body: data
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Projects', id }, { type: 'Projects', id: 'LIST' }],
        }),
        updateProjectStatus: build.mutation<void, { request: { id: string; state: ProjectStateEnum.CONCLUDED | ProjectStateEnum.INPROGRESS | undefined }[] }>({
            query: ({ request }) => ({
                url: `projects/state`,
                method: 'PUT',
                body: request
            }),
            invalidatesTags: (_result, _error, { request }) => [{ type: 'Projects', id: 'LIST' }],
        }),
    }),
});

export const {
    useFindAllProjectsQuery,
    useDeleteProjectMutation,
    useCreateProjectMutation,
    useFindOneProjectQuery,
    useEditProjectMutation,
    useBindCustomerMutation,
    useUpdateTeamLeaderMutation,
    useUpdateProjectStatusMutation,
    useFindSuspendedProjectsQuery,
} = projectApi